/**
 * RealtimeComposerBar
 *
 * Composer-adjacent chrome while Losi Inference realtime is active:
 * stop, mute, orb, voice picker, ⋯ mode menu. Keeps the chat transcript
 * visible (not a full-screen listening room).
 */

'use client'

import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react'
import {
  IconDotsVertical,
  IconMicrophone,
  IconMicrophoneOff,
  IconPaperclip,
  IconPlayerStop
} from '@tabler/icons-react'

import {
  getChatRealtimeLosiInferenceOptions,
  type LosiInferenceInteractionMode,
  type LosiInferenceSessionOptions
} from '@/lib/losi-inference'
import { openRealtimeLosiPage } from '@/lib/losi-inference/open-realtime-page'
import { cn } from '@/lib/utils'
import {
  useLosiInferenceVoice,
  type LosiInferenceAgentUiState,
  type LosiRealtimeDataEvent
} from '@/hooks/useLosiInferenceVoice'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { LosiInferenceVoicePicker } from '@/components/chat/losi-inference/losi-inference-voice-picker'
import { LosiRealtimeAura } from '@/components/chat/realtime/losi-realtime-aura'
import { toast } from 'sonner'

/** End realtime after this much silence while listening/idle. */
const REALTIME_IDLE_MS = 60_000
const REALTIME_IDLE_LEVEL_FLOOR = 0.04

export type RealtimeComposerBarProps = {
  chatId: string | null | undefined
  surface?: 'chat' | 'sidekick' | 'sidecar'
  isActive: boolean
  /** When true, continue the open text thread (skip cold greeting). */
  hasThreadHistory?: boolean
  /** Platform / tray tool ids already enabled for this chat. */
  enabledToolIds?: string[]
  onActiveChange: (active: boolean) => void
  onTranscript: (
    text: string,
    role: 'user' | 'assistant',
    meta?: { final: boolean; id?: string }
  ) => void
  onRealtimeEvent?: (event: LosiRealtimeDataEvent) => void
  /** Same-tab / split navigate when open_losi_page requests newTab:false. */
  onNavigate?: (path: string) => void
  onFilesSelect?: (files: File[]) => void | Promise<void>
  className?: string
}

export function RealtimeComposerBar({
  chatId,
  surface = 'chat',
  isActive,
  hasThreadHistory = false,
  enabledToolIds: enabledToolIdsProp,
  onActiveChange,
  onTranscript,
  onRealtimeEvent,
  onNavigate,
  onFilesSelect,
  className
}: RealtimeComposerBarProps) {
  const [inferenceOptions, setInferenceOptions] =
    useState<LosiInferenceSessionOptions>(getChatRealtimeLosiInferenceOptions)
  const [interactionMode, setInteractionMode] =
    useState<LosiInferenceInteractionMode>('full')
  const [speakOnlyDraft, setSpeakOnlyDraft] = useState('')
  const [agentState, setAgentState] =
    useState<LosiInferenceAgentUiState>('idle')
  const [sessionEnabledToolIds, setSessionEnabledToolIds] = useState<string[]>(
    () => (Array.isArray(enabledToolIdsProp) ? enabledToolIdsProp : [])
  )
  const sessionEnabledToolIdsRef = useRef(sessionEnabledToolIds)
  sessionEnabledToolIdsRef.current = sessionEnabledToolIds

  useEffect(() => {
    if (!Array.isArray(enabledToolIdsProp)) return
    setSessionEnabledToolIds(prev => {
      const merged = new Set([...prev, ...enabledToolIdsProp])
      const next = Array.from(merged)
      sessionEnabledToolIdsRef.current = next
      return next
    })
  }, [enabledToolIdsProp])
  const stopRef = useRef<() => Promise<void>>(async () => {})
  const onActiveChangeRef = useRef(onActiveChange)
  const autoStartedRef = useRef(false)
  /** Prevents overlapping auto-start / mode-reconnect while a start is in flight. */
  const startInFlightRef = useRef(false)
  useEffect(() => {
    onActiveChangeRef.current = onActiveChange
  }, [onActiveChange])

  const {
    isConnecting,
    isActive: sessionActive,
    isMuted,
    inputLevel,
    levelRef,
    start,
    stop,
    toggleMute,
    setRemoteAudioMuted,
    sendText
  } = useLosiInferenceVoice({
    onTranscript: (text, role, meta) => {
      if (!text.trim()) return
      onTranscript(text, role === 'user' ? 'user' : 'assistant', meta)
    },
    onAgentState: setAgentState,
    onRealtimeEvent: event => {
      onRealtimeEvent?.(event)
      // Worker refuse-to-start when chat hydrate left 0 tools (BUG6 worker side).
      if (event.type === 'tools_failed') {
        const msg =
          typeof event.message === 'string' && event.message.trim()
            ? event.message.trim()
            : 'Realtime tools failed to load. Try again, or continue in text chat.'
        toast.error(msg)
        void stopRef.current().then(() => onActiveChangeRef.current(false))
        return
      }
      if (event.type === 'navigate') {
        const path =
          typeof event.path === 'string' ? event.path.trim() : ''
        if (!path.startsWith('/') || path.startsWith('//')) return
        // Default new tab: keeps the realtime mic on this tab. Async
        // LiveKit packets often trip the popup blocker — helper always
        // offers a toast Open action so the click lands (BUG5).
        const newTab = event.newTab !== false
        openRealtimeLosiPage(path, {
          newTab,
          onNavigateInApp: onNavigate,
          label: path
        })
      }
      if (event.type === 'tool_enabled') {
        const toolId =
          typeof event.toolId === 'string' ? event.toolId.trim() : ''
        if (toolId) {
          setSessionEnabledToolIds(prev => {
            const next = prev.includes(toolId) ? prev : [...prev, toolId]
            sessionEnabledToolIdsRef.current = next
            return next
          })
          // Do NOT reconnect mid-call. Reconnect was wiping LiveKit chat
          // context and re-greeting ("Hey, I'm Losi…") mid-conversation.
          // invoke_platform_tool already runs any enabled platform function
          // without session restart; tray state updates via the event below.
        }
        try {
          window.dispatchEvent(
            new CustomEvent('losi:tool.enabled', {
              detail: {
                toolId: event.toolId,
                toolName: event.toolName
              }
            })
          )
        } catch {
          // ignore
        }
      }
      if (
        event.type === 'tool_end' &&
        (event.name === 'end_realtime' || event.name === 'end_call')
      ) {
        // Always tear down — hangup must work even if execute-tool marked ok:false.
        void stopRef.current().then(() => onActiveChangeRef.current(false))
      }
    },
    onError: err => {
      const raw = typeof err?.message === 'string' ? err.message.trim() : ''
      if (/VOICE_SERVER_SECRET|server config/i.test(raw)) {
        toast.error(
          "Realtime couldn't start. Check your connection or continue in text chat."
        )
        return
      }
      toast.error(
        raw || "Realtime hit a problem. Try again or continue in text chat."
      )
    },
    onConnected: session => {
      onActiveChange(true)
      if (
        typeof session.toolCount === 'number' &&
        session.toolCount === 0 &&
        (session.surface === 'chat' ||
          session.surface === 'sidekick' ||
          session.surface === 'sidecar' ||
          !session.surface)
      ) {
        toast.message("Couldn't load realtime tools", {
          description:
            'Voice still works. Search and other tools may be unavailable — continue in text chat if you need them.'
        })
      }
    },
    onDisconnected: () => {
      // Ignore disconnect callbacks that race an in-flight start (desktop
      // remount / pre-connect cleanup). Otherwise the bar exits realtime
      // while Connecting… and auto-start mints a second LiveKit room.
      if (startInFlightRef.current) return
      onActiveChange(false)
    }
  })

  stopRef.current = stop

  // Chat switch / New Chat must tear down the LiveKit session. Otherwise the
  // previous room keeps speaking while the UI shows a different chat (or
  // text mode), which looked like overlapping streams + mode leak.
  const boundChatIdRef = useRef<string | null | undefined>(chatId)
  useEffect(() => {
    const prev = boundChatIdRef.current
    if (prev === chatId) return
    // Skip the initial undefined→value mount so auto-start still owns connect.
    if (prev === undefined || prev === null) {
      boundChatIdRef.current = chatId
      if (!chatId) return
      // Coming from null (New Chat compose) onto a chatId: auto-start handles it.
      autoStartedRef.current = false
      return
    }
    // Transient null while /chat draft navigates to /chat/[id] — keep the
    // LiveKit room; do not end realtime or create a second chat.
    if (!chatId) {
      return
    }
    boundChatIdRef.current = chatId
    autoStartedRef.current = false
    let cancelled = false
    void (async () => {
      await stop()
      if (cancelled) return
      // Always leave realtime when leaving the chat that owns the session.
      onActiveChange(false)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chat identity only
  }, [chatId])

  useEffect(() => {
    // Parent deactivated realtime — tear down even while still connecting so
    // an in-flight mint/connect cannot finish into a stacked room.
    if (isActive) return
    if (!sessionActive && !isConnecting) return
    startInFlightRef.current = true
    void stop().finally(() => {
      startInFlightRef.current = false
      autoStartedRef.current = false
    })
  }, [isActive, sessionActive, isConnecting, stop])

  // Mid-call mode changes need a reconnect so the worker gets new metadata.
  // Do NOT mute remote audio before stop() — that kills TTS while the old
  // session keeps streaming transcripts (text/audio desync). Mute for
  // text_only is applied inside start() after the new room attaches.
  const modeReconnectSkipRef = useRef(true)
  useEffect(() => {
    if (!sessionActive) {
      modeReconnectSkipRef.current = true
      return
    }
    if (modeReconnectSkipRef.current) {
      modeReconnectSkipRef.current = false
      return
    }
    if (!chatId) return
    if (startInFlightRef.current) return
    let cancelled = false
    startInFlightRef.current = true
    void (async () => {
      // Tear down the live turn first so we don't mute mid-utterance on a
      // still-connected room (typing would continue with silent speakers).
      await stop()
      if (cancelled) {
        startInFlightRef.current = false
        return
      }
      try {
        const session = await start({
          ...inferenceOptions,
          chatId,
          surface,
          interactionMode,
          enabledToolIds: sessionEnabledToolIdsRef.current,
          // Mid-call mode switch must not re-greet like a new session.
          skipGreeting: true
        })
        if (cancelled) return
        if (!session) {
          // Stop (or a newer start) won the race — leave realtime inactive.
          onActiveChange(false)
          return
        }
        setRemoteAudioMuted(interactionMode === 'text_only')
      } catch {
        if (!cancelled) onActiveChange(false)
      } finally {
        startInFlightRef.current = false
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reconnect only on mode
  }, [interactionMode])

  const handleStart = useCallback(async () => {
    if (!chatId) {
      // New-chat wave mints a chat first — wait for chatId; auto-start retries.
      // Never toast “open a chat first”; realtime is how you start a chat.
      autoStartedRef.current = false
      return
    }
    if (startInFlightRef.current) return
    startInFlightRef.current = true
    try {
      const session = await start({
        ...inferenceOptions,
        chatId,
        surface,
        interactionMode,
        enabledToolIds: sessionEnabledToolIdsRef.current,
        // Continue the open text thread — no cold "Hey I'm Losi" intro.
        ...(hasThreadHistory ? { skipGreeting: true } : {})
      })
      // Stale/aborted start returns undefined (stop or newer start won).
      // Do not flip parent inactive — a remount's in-flight start may still
      // own realtime; clearing here caused first-start disconnect on desktop.
      if (!session) {
        autoStartedRef.current = false
        return
      }
      onActiveChange(true)
    } catch {
      // Failed mint/connect — reset so a later chatId/mode retry can fire.
      autoStartedRef.current = false
      onActiveChange(false)
    } finally {
      startInFlightRef.current = false
    }
  }, [
    chatId,
    hasThreadHistory,
    inferenceOptions,
    interactionMode,
    onActiveChange,
    start,
    surface
  ])

  useEffect(() => {
    if (!isActive) {
      autoStartedRef.current = false
      // Do NOT clear startInFlightRef here — an in-flight start/stop still
      // owns the mutex; clearing early allowed a second auto-start to stack.
    }
  }, [isActive])

  useEffect(() => {
    if (!isActive || sessionActive || isConnecting || !chatId) return
    if (autoStartedRef.current || startInFlightRef.current) return
    autoStartedRef.current = true
    void handleStart()
  }, [isActive, chatId, sessionActive, isConnecting, handleStart])

  const handleStop = useCallback(async () => {
    // Mark in-flight so auto-start cannot race a hangup, then invalidate
    // via stop() (bumps start generation + sweeps orphan audio).
    startInFlightRef.current = true
    try {
      await stop()
      onActiveChange(false)
      autoStartedRef.current = false
    } finally {
      startInFlightRef.current = false
    }
  }, [onActiveChange, stop])

  // Idle auto-stop: only while it is the USER's turn (listening/idle + quiet).
  // Pause while Losi is thinking/speaking so mid-response silence never ends
  // the session (BUG4).
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }, [])

  const armIdleTimer = useCallback(() => {
    clearIdleTimer()
    if (!sessionActive || isConnecting) return
    idleTimerRef.current = setTimeout(() => {
      void (async () => {
        await stopRef.current()
        onActiveChangeRef.current(false)
        toast.message('Realtime ended due to inactivity')
      })()
    }, REALTIME_IDLE_MS)
  }, [clearIdleTimer, isConnecting, sessionActive])

  useEffect(() => {
    if (!sessionActive || isConnecting) {
      clearIdleTimer()
      return
    }
    const losiBusy =
      agentState === 'speaking' || agentState === 'thinking'
    const userSpeaking = inputLevel >= REALTIME_IDLE_LEVEL_FLOOR
    if (losiBusy || userSpeaking) {
      // Pause countdown while someone is actively speaking / generating.
      clearIdleTimer()
      return
    }
    if (agentState === 'listening' || agentState === 'idle') {
      armIdleTimer()
    }
  }, [
    agentState,
    armIdleTimer,
    clearIdleTimer,
    inputLevel,
    isConnecting,
    sessionActive
  ])

  useEffect(() => {
    return () => {
      clearIdleTimer()
    }
  }, [clearIdleTimer])

  const handleSendTyped = useCallback(async () => {
    const text = speakOnlyDraft.trim()
    if (!text) return
    onTranscript(text, 'user', { final: true })
    await sendText(text)
    setSpeakOnlyDraft('')
    // User just acted — clear idle; Losi speaking will keep it paused.
    clearIdleTimer()
  }, [clearIdleTimer, onTranscript, sendText, speakOnlyDraft])

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFilesPicked = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const list = event.target.files
      if (!list || list.length === 0) return
      const files = Array.from(list)
      event.target.value = ''
      if (!onFilesSelect) {
        toast.message('Attachments unavailable in this view')
        return
      }
      try {
        await onFilesSelect(files)
      } catch {
        toast.error("Couldn't attach those files. Try again.")
      }
    },
    [onFilesSelect]
  )

  // Start control lives on the normal composer (waveform). Stay mounted only
  // while realtime is active so the chat transcript stays visible.
  if (!isActive && !sessionActive) {
    return null
  }

  const statusLabel = isConnecting
    ? 'Connecting…'
    : isMuted
      ? 'Muted'
      : agentState === 'speaking'
        ? 'Speaking…'
        : agentState === 'thinking'
          ? 'Thinking…'
          : 'Listening'

  return (
    <div
      className={cn(
        'bg-secondary/80 mb-2 flex w-full flex-col gap-2 rounded-xl border border-border/60 p-2',
        className
      )}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={event => void handleFilesPicked(event)}
        aria-hidden
        tabIndex={-1}
      />
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="icon"
          variant="destructive"
          className="size-10 shrink-0 rounded-full"
          onClick={() => void handleStop()}
          disabled={isConnecting}
          title="Stop realtime"
          aria-label="Stop realtime"
        >
          <IconPlayerStop size={20} />
        </Button>

        <Button
          type="button"
          size="icon"
          variant={isMuted ? 'default' : 'outline'}
          className="size-10 shrink-0 rounded-full"
          onClick={() => void toggleMute()}
          disabled={isConnecting || interactionMode === 'speak_only'}
          title={isMuted ? 'Unmute' : 'Mute'}
          aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
        >
          {isMuted ? (
            <IconMicrophoneOff size={20} />
          ) : (
            <IconMicrophone size={20} />
          )}
        </Button>

        <div className="flex flex-1 flex-col items-center justify-center gap-1">
          <LosiRealtimeAura
            agentState={agentState}
            connecting={isConnecting}
            muted={isMuted}
            levelRef={levelRef}
            size={56}
            showStatus={false}
          />
          <span className="text-muted-foreground text-[11px] font-medium tracking-wide">
            {statusLabel}
          </span>
        </div>

        <Button
          type="button"
          size="icon"
          variant="outline"
          className="size-10 shrink-0 rounded-full"
          onClick={handleAttachClick}
          disabled={isConnecting || !onFilesSelect}
          title="Attach"
          aria-label="Attach files"
        >
          <IconPaperclip size={18} />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="size-10 shrink-0 rounded-full"
              title="Realtime options"
              aria-label="Realtime options"
            >
              <IconDotsVertical size={18} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel>Mode</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={interactionMode}
              onValueChange={value =>
                setInteractionMode(value as LosiInferenceInteractionMode)
              }
            >
              <DropdownMenuRadioItem value="full">
                Full duplex (talk both ways)
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="text_only">
                Losi keeps quiet — you talk or type (no agent speech)
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="speak_only">
                Losi speaks — you type (mic off)
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Voice & brain</DropdownMenuLabel>
            <div className="px-2 pb-2">
              <LosiInferenceVoicePicker
                value={inferenceOptions}
                onChange={setInferenceOptions}
                disabled={sessionActive || isConnecting}
              />
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {interactionMode === 'text_only' && sessionActive ? (
        <p className="text-muted-foreground text-xs" role="status">
          Losi is quiet — replies show as text only (no speech).
        </p>
      ) : null}

      {interactionMode === 'speak_only' || interactionMode === 'text_only' ? (
        <div className="flex gap-2">
          <input
            className="border-border bg-background flex-1 rounded-md border px-3 py-2 text-sm"
            placeholder={
              interactionMode === 'speak_only'
                ? 'Type for Losi…'
                : 'Type (optional) while Losi stays silent…'
            }
            value={speakOnlyDraft}
            onChange={e => setSpeakOnlyDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void handleSendTyped()
              }
            }}
          />
          <Button type="button" onClick={() => void handleSendTyped()}>
            Send
          </Button>
        </div>
      ) : null}
    </div>
  )
}
